import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import authCopy from "../data/private/auth.json";
import { useAuth } from "../context/AuthContext";
import {
  resolvePostLoginRoute,
  usePlatformContext,
} from "../context/PlatformContext";
import { logActivity } from "../utils/activity";
import { getPlatformContext } from "../api/platformContext.api";

export default function OtpVerification() {
  const [otpValue, setOtpValue] = useState("");
  const [statusType, setStatusType] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [attemptsLeftMessage, setAttemptsLeftMessage] = useState("");
  const [attemptCount, setAttemptCount] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(300);
  const [isLocked, setIsLocked] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const platformRequestedRef = useRef(false);
  const navigate = useNavigate();
  const {
    verifyOtp,
    pendingOtpDelivery,
    pendingOtpLength,
  } = useAuth();
  const { setPlatformContext } = usePlatformContext();
  const otpLength = pendingOtpLength || 6;
  const isValid = otpValue.trim().length === otpLength;
  const maxAttempts = 3;
  const isExpired = timerSeconds <= 0;
  const limitReached = attemptCount >= maxAttempts;
  const screenLocked = isLocked || limitReached;
  const formattedTime = useMemo(() => {
    const minutes = Math.floor(timerSeconds / 60);
    const seconds = timerSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  }, [timerSeconds]);

  const deliveryHint = useMemo(() => {
    const channels = pendingOtpDelivery || [];
    if (channels.includes("email") && channels.includes("phone")) {
      return authCopy.otp.autoHint;
    }
    if (channels.includes("email")) {
      return "We've sent a verification code to your email.";
    }
    if (channels.includes("phone")) {
      return "We've sent a verification code to your phone.";
    }
    return authCopy.otp.autoHint;
  }, [pendingOtpDelivery]);

  useEffect(() => {
    if (timerSeconds <= 0) {
      return undefined;
    }
    const timer = setInterval(() => {
      setTimerSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [timerSeconds]);

  useEffect(() => {
    if (isExpired && statusType !== "success" && !screenLocked) {
      setStatusType("error");
      setStatusMessage(authCopy.otp.expiredMessage);
      setAttemptsLeftMessage("");
    }
  }, [isExpired, screenLocked, statusType]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isVerifying) {
      return;
    }
    if (screenLocked) {
      setStatusType("error");
      setStatusMessage(authCopy.otp.attemptsExceeded);
      setAttemptsLeftMessage("");
      return;
    }
    if (isExpired) {
      setStatusType("error");
      setStatusMessage(authCopy.otp.expiredMessage);
      setAttemptsLeftMessage("");
      return;
    }
    if (!isValid) {
      setStatusType("error");
      setStatusMessage(authCopy.otp.error);
      setAttemptsLeftMessage("");
      return;
    }
    setIsVerifying(true);
    setStatusType("");
    setStatusMessage("");
    setAttemptsLeftMessage("");
    const nextAttempts = attemptCount + 1;
    setAttemptCount(nextAttempts);
    const result = await verifyOtp(otpValue.trim());
    if (!result.ok) {
      if (result.code === "OTP_ATTEMPTS_EXCEEDED") {
        setIsLocked(true);
        setStatusType("error");
        setStatusMessage(authCopy.otp.attemptsExceeded);
      } else if (result.code === "OTP_EXPIRED") {
        setTimerSeconds(0);
        setStatusType("error");
        setStatusMessage(authCopy.otp.expiredMessage);
      } else if (result.code === "OTP_INVALID") {
        const remaining = Math.max(maxAttempts - nextAttempts, 0);
        setStatusType("error");
        setStatusMessage(authCopy.otp.error);
        if (remaining > 0) {
          setAttemptsLeftMessage(`${remaining} ${authCopy.otp.attemptsLeftSuffix}`);
        }
      } else if (result.code === "FEATURE_DISABLED") {
        setIsLocked(true);
        setStatusType("error");
        setStatusMessage(authCopy.otp.featureDisabled);
      } else {
        setStatusType("error");
        setStatusMessage(result.message || authCopy.otp.error);
      }
      setIsVerifying(false);
      return;
    }
    logActivity("auth_otp_verified", { otp: otpValue.trim() });
    setStatusType("success");
    setStatusMessage(authCopy.otp.success);
    if (platformRequestedRef.current) {
      setIsVerifying(false);
      return;
    }
    platformRequestedRef.current = true;
    const token = result?.data?.token || result?.data?.jwt;
    if (!token) {
      setStatusType("error");
      setStatusMessage(authCopy.otp.error);
      setIsVerifying(false);
      return;
    }
    try {
      const contextData = await getPlatformContext(token);
      if (!contextData) {
        throw new Error("Missing platform context");
      }
      setPlatformContext(contextData);
      const nextRoute = resolvePostLoginRoute(contextData);
      try {
        sessionStorage.setItem("myv_entry_point_used", "true");
      } catch (error) {
        // no-op
      }
      setTimeout(() => navigate(nextRoute, { replace: true }), 400);
    } catch (error) {
      setStatusType("error");
      setStatusMessage(authCopy.otp.error);
      setIsVerifying(false);
    }
  };
            <p className="text-xs text-slate-500">{deliveryHint}</p>
            {statusType === "success" && (
              <p className="text-sm text-emerald-600">
                {statusMessage || authCopy.otp.success}
              </p>
            )}

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="section-padding mx-auto w-full max-w-lg">
        <div className="card space-y-4 p-6">
          <h1 className="page-title text-2xl">{authCopy.otp.title}</h1>
          <p className="text-sm text-slate-600">{authCopy.otp.subtitle}</p>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="text-sm font-medium text-slate-700">
                {authCopy.otp.fieldLabel}
              </label>
              <input
                className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                placeholder={authCopy.otp.fieldPlaceholder}
                value={otpValue}
                onChange={(event) => setOtpValue(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label={authCopy.otp.fieldLabel}
                autoFocus
                maxLength={otpLength}
              />
            </div>
            {statusType === "error" && statusMessage && (
              <p className="text-sm text-rose-500">{statusMessage}</p>
            )}
            {attemptsLeftMessage && (
              <p className="text-xs text-rose-500">{attemptsLeftMessage}</p>
            )}
            {statusType === "error" && !statusMessage && (
              <p className="text-sm text-rose-500">{authCopy.otp.error}</p>
            )}
            <p className="text-xs text-slate-500">
              Time remaining: {formattedTime}
            </p>
            <button
              className="btn btn-primary w-full"
              disabled={!isValid || isExpired || screenLocked || isVerifying}
            >
              {authCopy.otp.submitLabel}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
